import React, { useEffect, useContext, useRef } from 'react'
import parse from 'html-react-parser'

import withLocation from '../../common/with-location'
import { useGetTopOfElementById } from '../../hooks'

import { StateContext } from './state-context'
import { Header } from './header'
import { Example } from './example'
import { Response } from './response'
import { Parameter } from './parameter'
import { Errors } from './errors'

export const Section = withLocation(({ section, groupIndex, Location, baseName }) => {
  const { getTopOfElementById } = useGetTopOfElementById()
  const { dispatch } = useContext(StateContext)
  const { title, description, type, url, header, parameter, success, error } = section
  const idsRef = useRef([])

  const idSuffix = url.substr(1).replace(/\//g, '-')
  const ids = idsRef.current

  useEffect(() => {
    const children = []
    ids.push(`title--${idSuffix}___${groupIndex}`)
    if (header) {
      children.push({ title: 'Header', id: `header--${idSuffix}___${groupIndex}` })
      ids.push(`header--${idSuffix}___${groupIndex}`)
    }
    if (parameter?.fields?.Parameter) {
      children.push({ title: 'Parameter', id: `parameters--${idSuffix}___${groupIndex}` })
      ids.push(`parameters--${idSuffix}___${groupIndex}`)
    }
    if (parameter?.examples) {
      children.push(
        ...parameter.examples.reduce((c, example, i) => {
          c.push({ title: example.title, id: `example--${idSuffix}-${i}___${groupIndex}` })
          ids.push(`example--${idSuffix}-${i}___${groupIndex}`)
          return c
        }, []),
      )
    }
    if (success?.fields?.Success_200) {
      children.push({ title: 'Response', id: `response--${idSuffix}___${groupIndex}` })
      ids.push(`response--${idSuffix}___${groupIndex}`)
    }
    if (success?.examples) {
      children.push(
        ...success.examples.reduce((c, example, i) => {
          c.push({ title: example.title, id: `success--${idSuffix}-${i}___${groupIndex}` })
          ids.push(`success--${idSuffix}-${i}___${groupIndex}`)
          return c
        }, []),
      )
    }
    if (error?.fields?.ErrorCode) {
      children.push({ title: 'Errors', id: `errors--${idSuffix}___${groupIndex}` })
      ids.push(`errors--${idSuffix}___${groupIndex}`)
    }
    const p = {
      title,
      id: `title--${idSuffix}___${groupIndex}`,
      groupIndex,
      children,
    }
    dispatch({ type: 'setMenu', payload: p })
  }, [
    ids,
    title,
    header,
    idSuffix,
    groupIndex,
    description,
    dispatch,
    parameter?.examples,
    parameter?.fields?.Parameter,
    success?.examples,
    success?.fields?.Success_200,
    error?.fields?.ErrorCode
  ])

  useEffect(() => {
    if (window) {
      window.addEventListener('scroll', () => {
        ids.some(id => {
          const top = getTopOfElementById(id)
          if (top <= 0 && top > -100) {
            window.history.pushState(
              '',
              `${baseName}`,
              `${Location.origin}${Location.pathname}#${id}`,
            )
            dispatch({
              type: 'setHash',
              payload: id,
            })
            const groupIndex = id.split('___')[1]
            if (groupIndex !== undefined) {
              dispatch({
                type: 'setGroupIndex',
                payload: +groupIndex,
              })
            }
            window.localStorage.setItem('groupIndex', +groupIndex)
          }
          return top <= 0 && top > -100
        })
      })
    }
  }, [ids, Location.origin, Location.pathname, baseName, dispatch, getTopOfElementById])

  return (
    <section id={`${idSuffix}___${groupIndex}`}>
      <h2 id={`title--${idSuffix}___${groupIndex}`}>{title}</h2>
      <summary id={`summary-${idSuffix}___${groupIndex}`}>
        {description ? parse(description) : ''}
      </summary>
      <span className='type'>{type}</span>
      <pre>{url}</pre>
      {header && (
        <Header headers={header.fields.Header} id={`header--${idSuffix}___${groupIndex}`} />
      )}
      {parameter?.fields?.Parameter && (
        <Parameter
          parameters={parameter.fields.Parameter}
          id={`parameters--${idSuffix}___${groupIndex}`}
        />
      )}
      {parameter?.examples &&
        parameter.examples.map((example, i) => (
          <Example
            key={example.title}
            example={example}
            id={`example--${idSuffix}-${i}___${groupIndex}`}
          />
        ))}
      {success?.fields?.Success_200 && (
        <Response
          responses={success.fields.Success_200}
          id={`response--${idSuffix}___${groupIndex}`}
        />
      )}
      {error?.fields?.ErrorCode && <Errors fields={error?.fields?.ErrorCode} id={`errors--${idSuffix}___${groupIndex}`} />}
      {success?.examples &&
        success.examples.map((example, i) => (
          <Example
            key={example.title}
            example={example}
            id={`success--${idSuffix}-${i}___${groupIndex}`}
          />
        ))}
    </section>
  )
})
